import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  createSelector
} from '@reduxjs/toolkit';

import { fetchAddCompany, fetchEditCompany, fetchGetCompanies } from 'api/companyApi';
import { fetchCustomerDetailsGraphQL } from 'api/graphql/customerDetailsGraphQL';
import { parseCustomerDetailsFromGraphQL } from 'services/customerDetailsGraphQLServices';
import showSnackbarError from 'utils/showSnackbarError';
import { updateUserProfile } from './authSlice';

const EMPTY_COMPANY = {
  id: null,
  name: '',
  owner: '',
  email: '',
  phone: ''
};

const initialState = {
  isLoaded: false,
  isLoading: false,
  hasError: false // Track if fetch failed to prevent incorrect redirects
};

const companiesAdapter = createEntityAdapter({
  selectId: (company) => company.id
});

export const getCustomerDetailsGraphQL = createAsyncThunk(
  'companies/getCustomerDetailsGraphQL',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const customerDetails = await fetchCustomerDetailsGraphQL();

      if (!customerDetails) {
        const { forceLoginRedirect } = await import('utils/forceLoginRedirect');
        forceLoginRedirect();
        return rejectWithValue('Session expired');
      }

      const { profile, company } = parseCustomerDetailsFromGraphQL(customerDetails);
      
      // Update user profile in auth state with customerId and stripeCustomerId
      if (profile) {
        const { customerId, stripeCustomerId, firstName, lastName, email } = profile;
        dispatch(updateUserProfile({ customerId, stripeCustomerId, firstName, lastName, email }));
      }
      
      // Return company as an array to match the existing structure
      return company ? [company] : [];
    } catch (e) {
      const message = e.message || '';
      if (
        message.includes('Session expired') ||
        message.includes('Unauthorized') ||
        message.includes('not authenticated')
      ) {
        const { forceLoginRedirect } = await import('utils/forceLoginRedirect');
        forceLoginRedirect();
      } else {
        dispatch(showSnackbarError(e.message, e.stack));
      }
      return rejectWithValue(e.message);
    }
  },
);

export const getCompanies = createAsyncThunk(
  'companies/getCompanies',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await fetchGetCompanies();

      return response;
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  },
);

export const addCompany = createAsyncThunk(
  'companies/addCompany',
  async ({ data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await fetchAddCompany(data);

      return response;
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  },
);

export const editCompany = createAsyncThunk(
  'companies/editCompany',
  async ({ data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await fetchEditCompany(data);

      return response;
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  },
);

export const companiesSlice = createSlice({
  name: 'companies',
  initialState: companiesAdapter.getInitialState(initialState),
  extraReducers: (builder) => {
    builder
      .addCase(getCustomerDetailsGraphQL.pending, (state) => {
        state.isLoading = true;
        state.isLoaded = false;
        state.hasError = false;
      })
      .addCase(getCustomerDetailsGraphQL.fulfilled, (state, action) => {
        companiesAdapter.setMany(state, action.payload);
        state.isLoading = false;
        state.isLoaded = true;
        state.hasError = false;
      })
      .addCase(getCustomerDetailsGraphQL.rejected, (state) => {
        state.isLoading = false;
        state.isLoaded = true;
        state.hasError = true; // Mark error to prevent incorrect setup-company redirect
      })
      .addCase(getCompanies.pending, (state) => {
        state.isLoading = true;
        state.isLoaded = false;
      })
      .addCase(getCompanies.fulfilled, (state, action) => {
        companiesAdapter.setMany(state, action.payload);
        state.isLoading = false;
        state.isLoaded = true;
      })
      .addCase(getCompanies.rejected, (state) => {
        state.isLoading = false;
        state.isLoaded = true;
      })
      .addCase(addCompany.fulfilled, (state, action) => {
        companiesAdapter.setOne(state, action.payload);
      })
      .addCase(editCompany.fulfilled, (state, action) => {
        companiesAdapter.setOne(state, action.payload);
      });
  }
});

const { selectAll, selectById } = companiesAdapter.getSelectors((state) => state.companies);

const all = createSelector(selectAll, (state) => state);

const byId = (id) => createSelector(
  (state) => state,
  (state) => selectById(state, id) || EMPTY_COMPANY,
);
const firstCompany = createSelector(selectAll, (state) => state[0] || EMPTY_COMPANY);

const selectSelf = (state) => state.companies;
const isLoading = createSelector(selectSelf, (state) => state.isLoading);
const isLoaded = createSelector(selectSelf, (state) => state.isLoaded);
const hasError = createSelector(selectSelf, (state) => state.hasError);

export const CompaniesSelectors = {
  all,
  firstCompany,
  byId,
  isLoading,
  isLoaded,
  hasError
};

export default companiesSlice.reducer;
